import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip } from "@mui/material";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import CreateNotificationCampaign from "./CreateNotificationCampaign";

function NotificationCampaigns() {
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [pagination, setPagination] = useState({});

  const fetchCampaigns = (url = null) => {
    setLoading(true);
    const fetchUrl = url || Urls.GET_NOTIFICATION_CAMPAIGNS;
    
    Api.get(fetchUrl, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      params: url ? {} : { perPage: 10 },
    })
      .then((res) => {
        setCampaigns(res?.data?.data || []);
        setPagination({
          links: res?.data?.links || {},
          meta: res?.data?.meta || {},
        });
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error loading campaigns");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleViewCampaign = (campaignId) => {
    navigate(`/admin/notifications/campaigns/${campaignId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "dispatched":
        return "success";
      case "scheduled":
        return "info";
      case "draft":
        return "default";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "dispatched":
        return "Sent";
      case "scheduled":
        return "Scheduled";
      case "draft":
        return "Draft";
      case "failed":
        return "Failed";
      default:
        return status;
    }
  };

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontSize="18px" fontWeight="500" color="#4B465C">
          Notification Campaigns
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          New Campaign
        </Button>
      </Box>
      <Divider />
      
      {formOpen && (
        <CreateNotificationCampaign
          access_token={access_token}
          onClose={() => {
            setFormOpen(false);
            fetchCampaigns();
          }}
        />
      )}

      {loading ? (
        <Box mt={4} display="flex" justifyContent="center">
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <Box mt={2}>
          {campaigns.length === 0 ? (
            <Box 
              mt={4} 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              sx={{ minHeight: "300px" }}
            >
              <Typography variant="h6" color="text.secondary" mb={2}>
                No campaigns created
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create your first notification campaign to get started
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setFormOpen(true)}
              >
                Create Campaign
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Target Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>{campaign.title}</TableCell>
                      <TableCell>
                        {campaign.target_type === "all_clients" && "All Clients"}
                        {campaign.target_type === "all_coaches" && "All Coaches"}
                        {campaign.target_type === "all_admins" && "All Admins"}
                        {campaign.target_type === "specific_users" && "Specific Users"}
                        {campaign.target_type === "program_duration" && "Program"}
                        {campaign.target_type === "court_weekly_schedule" && "Weekly Schedule"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(campaign.status)}
                          color={getStatusColor(campaign.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{campaign.recipients_count || 0}</TableCell>
                      <TableCell>
                        {new Date(campaign.created_at).toLocaleDateString("en-US")}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewCampaign(campaign.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {pagination.links?.next && (
            <Box mt={2} display="flex" justifyContent="center">
              <Button
                onClick={() => fetchCampaigns(pagination.links.next)}
                variant="outlined"
              >
                Load More
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default NotificationCampaigns;

